// Motes: a dissolving ragdoll breaks into small glowing flecks that rise, drift and shrink away (decision 0057's
// "dissolves into motes"). One instanced mesh on the look's surface material (glowing faces, a colour per mote), made
// before the look warms up so it compiles at boot. Deliberately small and replaceable: the feedback stream's
// `@rpg/vfx` particles take over at the merge. A view: seeded per burst, stepped on the frame's time.
import * as THREE from "three";
import { facet, seeded } from "@rpg/meshkit";

/** The motes. */
export interface Motes {
  mesh: THREE.InstancedMesh;
  /** Release `count` motes spread over `points` (world), in `colour`, from a seed. */
  burst(points: readonly THREE.Vector3[], count: number, colour: THREE.Color, seed: number): void;
  /** Move them on by `dt` seconds. */
  update(dt: number): void;
  /** Motes alive now. */
  alive(): number;
}

interface Mote {
  p: THREE.Vector3;
  v: THREE.Vector3;
  life: number;
  age: number;
  size: number;
  spin: number;
}

/** Make the motes (at most `max` alive) on `material` (the look's surface), added under `parent`. */
export function createMotes(parent: THREE.Object3D, material: THREE.Material, max = 384): Motes {
  const geo = facet(new THREE.OctahedronGeometry(0.035, 0), { colour: new THREE.Color("#ffffff"), glow: 1, jitter: 0 });
  const mesh = new THREE.InstancedMesh(geo, material, max);
  mesh.name = "motes";
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  for (let i = 0; i < max; i++) {
    mesh.setMatrixAt(i, zero);
    mesh.setColorAt(i, new THREE.Color("#ffffff"));
  }
  parent.add(mesh);
  const motes: (Mote | null)[] = Array.from({ length: max }, () => null);
  let cursor = 0;
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0);
  return {
    mesh,
    burst(points, count, colour, seed) {
      if (!points.length) return;
      const rng = seeded(seed);
      for (let k = 0; k < count; k++) {
        const at = points[k % points.length]!;
        const i = cursor;
        cursor = (cursor + 1) % max;
        motes[i] = {
          p: at.clone().add(new THREE.Vector3((rng() - 0.5) * 0.3, (rng() - 0.5) * 0.25, (rng() - 0.5) * 0.3)),
          v: new THREE.Vector3((rng() - 0.5) * 0.5, 0.5 + rng() * 0.9, (rng() - 0.5) * 0.5),
          life: 0.9 + rng() * 0.9,
          age: 0,
          size: 0.6 + rng() * 0.9,
          spin: rng() * 6,
        };
        mesh.setColorAt(i, colour);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    update(dt) {
      for (let i = 0; i < max; i++) {
        const o = motes[i];
        if (!o) continue;
        o.age += dt;
        if (o.age >= o.life) {
          motes[i] = null;
          mesh.setMatrixAt(i, zero);
          continue;
        }
        o.v.multiplyScalar(Math.exp(-1.5 * dt));
        o.v.y += 0.6 * dt; // they lift as they fade
        o.p.addScaledVector(o.v, dt);
        const k = 1 - o.age / o.life;
        s.setScalar(o.size * k * (0.6 + 0.4 * Math.sin(o.age * 9 + o.spin)));
        mesh.setMatrixAt(i, m.compose(o.p, q.setFromAxisAngle(UP, o.spin + o.age * 3), s));
      }
      mesh.instanceMatrix.needsUpdate = true;
    },
    alive: () => motes.filter(Boolean).length,
  };
}
